import TextArea from '@atlaskit/textarea';
import { submitFormOnEnter } from '@databases/form/Form';
import { DatabaseFieldType, NumberFormats } from '@databases/types/database';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { Container } from './TextValueEdit';
import { type EditComponentProps } from './types';

const NumberValueContainer = styled(Container)`
  * {
    text-align: right;
  }
`;

const NumberValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.NUMBER>
> = ({ onChange, onFocus, onBlur, autoFocus, value, isInvalid, field, initialKey }) => {
  const { locale } = useIntl();

  const [inputValue, setInputValue] = useState(
    initialKey ?? (value !== '' ? getNumberDisplayString(value, field, locale) : '')
  );

  useEffect(() => {
    if (initialKey !== undefined) {
      onChange(initialKey, { type: DatabaseFieldType.NUMBER, value: initialKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function sanitizeNumberInputValue(inputValue: string): string {
    const isNegative = inputValue.startsWith('-');

    const sanitizedInput = inputValue.replace(/[^0-9.,]/g, '');

    return isNegative ? '-' + sanitizedInput : sanitizedInput;
  }

  const parseInput = useCallback(
    (localizedString: string) => {
      const formatter = new Intl.NumberFormat(locale);

      const parts = formatter.formatToParts(12345.6);
      let decimalSeparator = '.';
      let groupingSeparator = ',';
      for (const part of parts) {
        if (part.type === 'decimal') {
          decimalSeparator = part.value;
        } else if (part.type === 'group') {
          groupingSeparator = part.value;
        }
      }

      let withoutGroupingSeparators = localizedString.split(groupingSeparator).join('');

      // Take only the first occurrence of the decimal separator
      const decimalIndex = withoutGroupingSeparators.indexOf(decimalSeparator);
      if (decimalIndex !== -1) {
        withoutGroupingSeparators =
          withoutGroupingSeparators.slice(0, decimalIndex) +
          '.' +
          withoutGroupingSeparators
            .slice(decimalIndex + 1)
            .split(decimalSeparator)
            .join('');
      }

      return sanitizeNumberInputValue(withoutGroupingSeparators);
    },
    [locale]
  );

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>): void => {
      let parsedValue = parseInput(e.currentTarget.value);
      setInputValue(e.currentTarget.value);
      if (field.settings.numberFormat.format === NumberFormats.PERCENT) {
        parsedValue = (Number(parsedValue) / 100).toString();
      }

      onChange(parsedValue, {
        type: DatabaseFieldType.NUMBER,
        value: parsedValue,
      });
    },
    [parseInput, field.settings.numberFormat.format, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => submitFormOnEnter(e),
    []
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    setInputValue(value !== '' ? getNumberDisplayString(value, field, locale) : '');
  }, [field, locale, onBlur, value]);

  return (
    <NumberValueContainer>
      <TextArea
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isInvalid={isInvalid}
        autoFocus={autoFocus}
        onKeyDown={handleKeyDown}
        rows={1}
      />
    </NumberValueContainer>
  );
};

export default NumberValueEdit;
